/**
 * BOSS — görev takip modalı.
 * Tarih seçici + o tarihte göreve hedeflenen TÜM personel.
 * Her kişi: son durum + tüm denemeler (foto/açıklama) + Reddet (sebep zorunlu)
 * + devam eden görevde Onayla.
 *
 * Parça C: süresi geçmiş + işaretlenmemiş → "Kaçırıldı" (türetilmiş, KARAR #24).
 */

import { useEffect, useState, useCallback } from 'react'
import {
  X, CheckCircle2, XCircle, Hourglass, Circle, RotateCcw,
  AlertTriangle,
} from 'lucide-react'
import {
  gorevGununDurumu, gorevKartTarihi, haftaBasiHesapla,
  gorevKapatmaOnayla, gorevTamamlamaReddet,
  type GorevDoc, type GorevKisiDurum,
} from '@/lib/services/gorev-service'
import { haftaVardiyalariGetir, type Vardiya } from '@/lib/services/vardiya-service'
import type { Sube, KullaniciPersonel, GorevTamamlamasi } from '@/types'

interface Props {
  acik: boolean
  gorev: GorevDoc | null
  personeller: KullaniciPersonel[]
  subeler: Sube[]
  tumTamamlama: GorevTamamlamasi[]
  onaylayanId: string
  onKapat: () => void
  onDegisti: () => void
}

export function GorevTakipModal({
  acik, gorev, personeller, subeler, tumTamamlama, onaylayanId, onKapat, onDegisti,
}: Props) {
  const [tarih, setTarih] = useState('')
  const [vardiyalar, setVardiyalar] = useState<Vardiya[]>([])
  const [yukleniyor, setYukleniyor] = useState(false)
  const [islemde, setIslemde] = useState<string | null>(null)
  const [buyukFoto, setBuyukFoto] = useState<string | null>(null)
  const [redKutu, setRedKutu] = useState<string | null>(null) // tamamlamaId
  const [redSebep, setRedSebep] = useState('')

  useEffect(() => {
    if (acik && gorev) {
      setTarih(gorevKartTarihi(gorev))
      setRedKutu(null)
      setRedSebep('')
    }
  }, [acik, gorev])

  const vardiyaYukle = useCallback(async () => {
    if (!gorev || !tarih) return
    if (!gorev.zamanAraligi || gorev.mesaiDisiDahil) { setVardiyalar([]); return }
    try {
      setYukleniyor(true)
      const hafta = haftaBasiHesapla(tarih)
      const hepsi: Vardiya[] = []
      for (const s of subeler) {
        const v = await haftaVardiyalariGetir(hafta, s.id)
        hepsi.push(...v)
      }
      setVardiyalar(hepsi)
    } catch {
      setVardiyalar([])
    } finally {
      setYukleniyor(false)
    }
  }, [gorev, tarih, subeler])

  useEffect(() => { vardiyaYukle() }, [vardiyaYukle])

  if (!acik || !gorev) return null

  const liste: GorevKisiDurum[] = gorevGununDurumu(
    gorev, tarih, personeller, vardiyalar, tumTamamlama
  )

  const say = {
    tamam: liste.filter((x) => x.durum === 'tamamlandi').length,
    yapilamadi: liste.filter((x) => x.durum === 'yapilamadi').length,
    red: liste.filter((x) => x.durum === 'reddedildi').length,
    kacirildi: liste.filter((x) => x.durum === 'kacirildi').length,
    bekleyen: liste.filter((x) => x.durum === 'beklemede').length,
    acik: liste.filter((x) => x.durum === 'bekliyor').length,
  }

  async function onayla(t: GorevTamamlamasi) {
    setIslemde(t.id)
    try {
      await gorevKapatmaOnayla(t.id, onaylayanId)
      onDegisti()
    } catch (err) {
      alert('Hata: ' + (err as Error).message)
    } finally {
      setIslemde(null)
    }
  }

  async function reddet(t: GorevTamamlamasi) {
    if (!redSebep.trim()) { alert('Red sebebi zorunlu.'); return }
    setIslemde(t.id)
    try {
      await gorevTamamlamaReddet(t.id, onaylayanId, redSebep.trim())
      setRedKutu(null)
      setRedSebep('')
      onDegisti()
    } catch (err) {
      alert('Hata: ' + (err as Error).message)
    } finally {
      setIslemde(null)
    }
  }

  function durumEtiket(d: GorevKisiDurum['durum']) {
    if (d === 'tamamlandi') return 'Tamamladı'
    if (d === 'yapilamadi') return 'Yapamadı'
    if (d === 'reddedildi') return 'Reddedildi'
    if (d === 'kacirildi') return 'Kaçırıldı'
    if (d === 'beklemede') return 'Onay bekliyor'
    return 'Henüz yapmadı'
  }

  function zaman(t: GorevTamamlamasi): string {
    return t.olusturmaTarihi?.toDate
      ? t.olusturmaTarihi.toDate().toLocaleString('tr-TR', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        })
      : (t.tarih as unknown as string)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 truncate">{gorev.baslik}</h2>
            <p className="text-xs text-gray-500">
              {say.tamam} tamam · {say.yapilamadi} yapılamadı · {say.red} red · {say.kacirildi} kaçırıldı · {say.acik} bekliyor
            </p>
          </div>
          <button onClick={onKapat} className="p-1 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Tarih</label>
            <input
              type="date"
              value={tarih}
              onChange={(e) => setTarih(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {yukleniyor ? (
            <div className="text-center py-10">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            </div>
          ) : liste.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-600">
              Bu tarihte bu görev kimseyi kapsamıyor.
            </div>
          ) : (
            <div className="space-y-2">
              {liste.map(({ personel, tamamlama, durum, denemeler }) => (
                <div
                  key={personel.id}
                  className={`border rounded-xl p-3 ${
                    durum === 'tamamlandi' ? 'bg-green-50 border-green-200'
                    : durum === 'yapilamadi' ? 'bg-red-50 border-red-200'
                    : durum === 'reddedildi' ? 'bg-orange-50 border-orange-300'
                    : durum === 'kacirildi' ? 'bg-red-50 border-red-300'
                    : durum === 'beklemede' ? 'bg-blue-50 border-blue-200'
                    : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {durum === 'tamamlandi' && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />}
                    {durum === 'yapilamadi' && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    {durum === 'reddedildi' && <XCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />}
                    {durum === 'kacirildi' && <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    {durum === 'beklemede' && <Hourglass className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                    {durum === 'bekliyor' && <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                    <span className="font-semibold text-sm text-gray-900">{personel.isim}</span>
                    <span className="text-[11px] text-gray-500 ml-auto">{durumEtiket(durum)}</span>
                  </div>

                  {durum === 'kacirildi' && (
                    <p className="text-xs text-red-800 mt-2 bg-red-100 rounded p-1.5">
                      ⏰ Süresi geçti, işaretlenmedi. Otomatik ceza yok — karar sizde.
                    </p>
                  )}

                  {/* Deneme geçmişi (yeni → eski) */}
                  {denemeler.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {denemeler.map((t, i) => (
                        <div key={t.id} className="border border-gray-200 rounded-lg p-2 bg-white">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                            <span className="font-semibold text-gray-700">
                              {denemeler.length > 1 ? `Deneme ${denemeler.length - i}` : 'Kayıt'}
                            </span>
                            <span>·</span>
                            <span>
                              {t.durum === 'tamamlandi' && 'Tamamladı'}
                              {t.durum === 'yapilamadi' && 'Yapamadı'}
                              {(t.durum as string) === 'reddedildi' && 'Reddedildi'}
                              {t.durum === 'beklemede' && 'Onay bekliyor'}
                            </span>
                            <span className="ml-auto">{zaman(t)}</span>
                          </div>
                          {t.aciklama && (
                            <p className="text-xs text-gray-700 mt-1 italic">"{t.aciklama}"</p>
                          )}
                          {(t.durum as string) === 'reddedildi' && t.redSebebi && (
                            <p className="text-xs text-orange-800 mt-1 bg-orange-100 rounded p-1.5">
                              Red sebebi: {t.redSebebi}
                            </p>
                          )}
                          {(() => {
                            const fotolar = t.fotoUrllar ?? (t.fotoUrl ? [t.fotoUrl] : [])
                            if (fotolar.length === 0) return null
                            return (
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {fotolar.map((url, fi) => (
                                  <button
                                    key={fi}
                                    onClick={() => setBuyukFoto(url)}
                                    className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-200 hover:opacity-80"
                                  >
                                    <img src={url} alt="" className="w-full h-full object-cover" />
                                  </button>
                                ))}
                              </div>
                            )
                          })()}

                          {/* Eylemler — sadece en güncel kayıt için */}
                          {i === 0 && (t.durum === 'tamamlandi' || t.durum === 'beklemede') && (
                            <div className="mt-2">
                              {redKutu === t.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={redSebep}
                                    onChange={(e) => setRedSebep(e.target.value)}
                                    rows={2}
                                    placeholder="Red sebebi (zorunlu) — örn. Dolabı tam doldurmamışsın, tekrar gönder"
                                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      disabled={islemde === t.id}
                                      onClick={() => reddet(t)}
                                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold py-1.5 rounded-lg disabled:opacity-50"
                                    >
                                      {islemde === t.id ? '…' : 'Reddet ve geri gönder'}
                                    </button>
                                    <button
                                      onClick={() => { setRedKutu(null); setRedSebep('') }}
                                      className="flex-1 border border-gray-300 text-gray-600 text-xs font-semibold py-1.5 rounded-lg"
                                    >
                                      Vazgeç
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  {t.durum === 'beklemede' && (
                                    <button
                                      disabled={islemde === t.id}
                                      onClick={() => onayla(t)}
                                      className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold py-1.5 rounded-lg disabled:opacity-50"
                                    >
                                      {islemde === t.id ? '…' : 'Onayla (görevi kapat)'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => { setRedKutu(t.id); setRedSebep('') }}
                                    className="flex-1 border border-orange-300 text-orange-700 hover:bg-orange-50 text-xs font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1"
                                  >
                                    <RotateCcw className="w-3 h-3" /> Reddet
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {buyukFoto && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setBuyukFoto(null)}
        >
          <img src={buyukFoto} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  )
}
